import Handlebars from "handlebars";

/**
 * Handlebars helper to look up a key in an object and return the value.
 * If the key does not exist, return a default value.
 *
 * @param {Object} object - The object to look up the key in
 * @param {string} key - The key to lookup in the object
 * @param {*} defaultValue - The default value to return if the key does not exist
 *
 * @returns {*} The value of the key in the object or the default value
 */
Handlebars.registerHelper("get", (object, key, ...args) => {
  args.pop(); // Remove the options object
  const defaultValue = args.pop();

  return object?.[key] ?? defaultValue ?? object;
});

/**
 * Handlebars helper to spread an object into HTML attributes.
 * Optionally exclude certain attributes from being spread.
 *
 * Usage:
 * ```js
 * const attributes = {
 *  class: "btn btn-primary",
 *  id: "submit-button",
 *  type: "submit",
 * }
 * ```
 *
 * ```handlebars
 * <button {{spread attributes except="id"}}>Submit</button>
 * ```
 *
 * Output:
 * ```html
 * <button class="btn btn-primary" type="submit">Submit</button>
 * ```
 *
 * @param {Object} attributes - The attributes to spread
 * @param {Object} options - The options object
 * @param {string} options.hash.except - A comma-separated list of attributes to exclude
 *
 * @returns {SafeString} The attributes as a string
 */
Handlebars.registerHelper("spread", (attributes, options) => {
  if (!attributes) {
    return "";
  }

  // Get the "except" option if provided and split it into an array
  const excludeAttributes = options?.hash?.except?.split(",").map((attr) => attr.trim()) ?? [];

  // Filter out the excluded attributes and create the attribute string
  const attributeString = Object.entries(attributes)
    .filter(([key]) => !excludeAttributes.includes(key))
    .map(([key, value]) => `${Handlebars.escapeExpression(key)}="${Handlebars.escapeExpression(value)}"`)
    .join(" ");

  return new Handlebars.SafeString(attributeString);
});
